import React from 'react';
import { ArrowRight, Save, Sparkles } from 'lucide-react';
import type { ToolCtaCopy } from '../../lib/toolCopy';

interface ToolResultCtaProps {
  copy: ToolCtaCopy;
  isSignedIn: boolean;
  onPrimary: () => void;
  onSaveDraft: () => void;
  onBrowse: () => void;
}

/**
 * Contextual CTA shown after the user has actually changed inputs (or returned
 * to a restored draft). It replaces the old 45-second timer as the main bridge
 * from the result to the product.
 */
export function ToolResultCta({
  copy,
  isSignedIn,
  onPrimary,
  onSaveDraft,
  onBrowse,
}: ToolResultCtaProps) {
  const isOperator = copy.segment === 'operator';

  return (
    <div className="mt-8 border-[4px] border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white shadow-[8px_8px_0px_#0044FF] p-5 md:p-6 print:hidden">
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-11 h-11 shrink-0 bg-[#0044FF] text-white border-[3px] border-black dark:border-white flex items-center justify-center shadow-[3px_3px_0px_#000]">
            <Sparkles size={20} />
          </div>
          <div>
            <p className="text-[10px] font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600]">
              {copy.eyebrow}
            </p>
            <h3 className="text-xl md:text-2xl font-black font-display uppercase tracking-tight leading-none mt-1">
              {copy.headline}
            </h3>
            <p className="text-sm font-bold text-gray-600 dark:text-gray-400 mt-2 max-w-2xl leading-relaxed">
              {copy.body}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-auto shrink-0">
          <button
            type="button"
            onClick={isOperator ? onPrimary : onBrowse}
            className="bg-[#0044FF] hover:bg-blue-700 text-white border-[3px] border-black dark:border-white font-black uppercase tracking-wider px-5 py-3 text-xs shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2 cursor-pointer"
          >
            {copy.primaryCta}
            <ArrowRight size={15} strokeWidth={3} />
          </button>

          {isOperator && (
            <button
              type="button"
              onClick={onSaveDraft}
              className="bg-[#FFD600] text-black border-[3px] border-black dark:border-white font-black uppercase tracking-wider px-4 py-3 text-xs shadow-[4px_4px_0px_#000] hover:bg-[#E6C200] transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <Save size={14} strokeWidth={3} />
              Save Draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
